import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Client } from 'src/providers/client/client';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import {
  RESTOCKING_CHECKLIST_GROUPS,
  RESTOCKING_HOME_SIZES,
  RestockingChecklistItem,
  RestockingHomeSize,
  RestockingHomeSizeOption,
  RestockingInputsSnapshot,
  RestockingItemSizes,
} from 'src/shared/constants/onboarding/restocking-checklist';
import { CheckboxModel } from 'src/tidy-ui-components/components/form/checkbox/checkbox.model';

type ItemFormGroup = FormGroup<{
  have: FormControl<boolean>;
  quantity: FormControl<string>;
  details: FormControl<string>;
}>;

interface RestockingItemVm {
  id: string;
  name: string;
  requiresQuantity: boolean;
  sizes: RestockingItemSizes;
  // Raw recommendation for the selected home size ('2 sets') and its row label ('2 sets recommended').
  recommendedValue: string | null;
  recommendedLabel: string | null;
  checkboxItems: CheckboxModel[];
  form: ItemFormGroup;
}

interface RestockingGroupVm {
  label: string;
  items: RestockingItemVm[];        // every item in the group
  visibleItems: RestockingItemVm[]; // items applicable to the selected home size
}

/**
 * Restocking onboarding step (Funnel V2). Two-step wizard:
 *   1. Bed / bath count for the property.
 *   2. Grouped checklist of what the property already has — checking an item reveals a
 *      required quantity field (for countable items) and an optional details field.
 *
 * On submit it saves the full snapshot to `onboarding_step.{addressId}.restocking_inputs`
 * (JSON, read by the concierge Account Setup "View Inputs" modal) and writes
 * `onboarding_step.{addressId}.restocking = completed` — the signal both the client's
 * Get Started list and the concierge board read as "done".
 *
 * Distinct from {@link RestockingOnboardingComponent} (route `onboarding/inventoryAutomation`),
 * which is the inventory-automation flow and is NOT reused here.
 */
@Component({
  selector: 'restocking-inputs-onboarding',
  templateUrl: './restocking-onboarding.component.html',
  styleUrls: ['./restocking-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RestockingInputsOnboardingComponent implements OnInit, OnDestroy {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  windowService = inject(WindowService);

  private addressId: number;
  private readonly stepId = 'restocking';

  loaded = false;
  fromGettingStarted = false;
  isSaving = false;
  submitted = false;
  currentStep: 1 | 2 = 1;

  homeSizes = RESTOCKING_HOME_SIZES;
  selectedHomeSize: RestockingHomeSize | null = null;
  groupVms: RestockingGroupVm[] = [];

  get progressPercent(): number {
    return Math.round((this.currentStep / 2) * 100);
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.rightSidePanelService.setDialogPageTitle('Restocking');
    this.buildGroupVms();
    await this.prefillFromSavedInputs();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  private buildGroupVms() {
    this.groupVms = RESTOCKING_CHECKLIST_GROUPS.map((group) => ({
      label: group.label,
      items: group.items.map((item) => this.buildItemVm(item)),
      visibleItems: [],
    }));
  }

  private buildItemVm(item: RestockingChecklistItem): RestockingItemVm {
    return {
      id: item.id,
      name: item.name,
      requiresQuantity: item.requiresQuantity,
      sizes: item.sizes,
      recommendedValue: null,
      recommendedLabel: null,
      checkboxItems: [{ value: item.id, viewValue: item.name }],
      form: this.fb.group({
        have: this.fb.nonNullable.control(false),
        quantity: this.fb.nonNullable.control(''),
        details: this.fb.nonNullable.control(''),
      }),
    };
  }

  // Filter each group to the items applicable to the chosen bed / bath count and set the
  // per-size recommendation label ("2 sets recommended"). Items with no entry for the
  // selected size are hidden entirely (a 1BR guest never sees the 3BR-only extras).
  private applyHomeSize(homeSize: RestockingHomeSize) {
    for (const group of this.groupVms) {
      group.visibleItems = group.items.filter((vm) => vm.sizes[homeSize] !== null && vm.sizes[homeSize] !== undefined);
      for (const vm of group.visibleItems) {
        const recommended = vm.sizes[homeSize];
        vm.recommendedValue = typeof recommended === 'string' ? recommended : null;
        vm.recommendedLabel = typeof recommended === 'string' ? `${recommended} recommended` : null;
      }
    }
  }

  // Re-entry after a previous submission: restore the saved home size and answers so the
  // user edits what they sent instead of starting over.
  private async prefillFromSavedInputs() {
    if (!this.addressId) { return; }
    try {
      const key = `external.onboarding_step.${this.addressId}.restocking_inputs`;
      const metadata: any[] = await this.client.getMetadata([key]);
      const row = Array.isArray(metadata) ? metadata.find((m) => m?.key === key) : null;
      if (!row?.value) { return; }
      const snapshot: RestockingInputsSnapshot = JSON.parse(row.value);
      const homeSize = this.homeSizes.find((s) => s.value === snapshot.homeSize);
      if (!homeSize) { return; }
      const savedItems = new Map<string, RestockingInputsSnapshot['groups'][number]['items'][number]>();
      for (const group of snapshot.groups || []) {
        for (const item of group.items || []) {
          savedItems.set(item.id, item);
        }
      }
      for (const group of this.groupVms) {
        for (const vm of group.items) {
          const saved = savedItems.get(vm.id);
          if (!saved) { continue; }
          vm.form.patchValue({
            have: !!saved.have,
            quantity: saved.quantity !== null && saved.quantity !== undefined ? String(saved.quantity) : '',
            details: saved.details || '',
          });
          this.syncQuantityValidators(vm);
        }
      }
      this.selectHomeSize(homeSize);
    } catch {
      // Non-fatal — start the wizard fresh.
    }
  }

  selectHomeSize = (option: RestockingHomeSizeOption) => {
    this.selectedHomeSize = option.value;
    this.applyHomeSize(option.value);
    this.currentStep = 2;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  toggleHave = (item: RestockingItemVm, checked: boolean) => {
    item.form.controls.have.setValue(checked);
    this.syncQuantityValidators(item);
    this.cdr.markForCheck();
  };

  // Quantity is required (whole number ≥ 1) only while the item is checked and countable.
  private syncQuantityValidators(item: RestockingItemVm) {
    const quantity = item.form.controls.quantity;
    if (item.form.controls.have.value && item.requiresQuantity) {
      quantity.setValidators([Validators.required, Validators.pattern(/^[0-9]+$/), Validators.min(1)]);
    } else {
      quantity.clearValidators();
    }
    quantity.updateValueAndValidity();
  }

  private updateBackAction() {
    if (this.currentStep > 1) {
      this.rightSidePanelService.customBackAction$.next(() => {
        this.currentStep = 1;
        this.cdr.markForCheck();
        this.updateBackAction();
      });
    } else {
      this.rightSidePanelService.customBackAction$.next(null);
    }
  }

  submit = async () => {
    if (!this.selectedHomeSize) { return; }
    this.submitted = true;
    // Only the items visible for the chosen home size are answerable, so validate those.
    const invalid = this.groupVms.some((group) => group.visibleItems.some((item) => item.form.controls.quantity.invalid));
    if (invalid) {
      this.cdr.markForCheck();
      this.util.showError('There are errors above. Please scroll up and fix the highlighted fields.');
      return;
    }
    if (this.isSaving) { return; }
    this.isSaving = true;
    this.cdr.markForCheck();
    try {
      await this.client.addMetadata({
        key: `onboarding_step.${this.addressId}.restocking_inputs`,
        value: JSON.stringify(this.buildSnapshot(this.selectedHomeSize)),
      });
      await this.markStepCompleted();
      this.navigateBack();
    } catch (err) {
      this.util.showError('Failed to save. Please try again.');
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  };

  private buildSnapshot(homeSize: RestockingHomeSize): RestockingInputsSnapshot {
    const homeSizeOption = this.homeSizes.find((s) => s.value === homeSize);
    return {
      v: 1,
      homeSize,
      homeSizeLabel: homeSizeOption?.viewValue || '',
      submittedAt: new Date().toISOString(),
      // Only groups/items applicable to the chosen home size are persisted, so the
      // concierge modal shows exactly the size-appropriate list.
      groups: this.groupVms
        .filter((group) => group.visibleItems.length > 0)
        .map((group) => ({
        label: group.label,
        items: group.visibleItems.map((vm) => {
          const value = vm.form.getRawValue();
          const item: RestockingInputsSnapshot['groups'][number]['items'][number] = {
            id: vm.id,
            name: vm.name,
            have: value.have,
          };
          if (value.have && vm.requiresQuantity) {
            item.quantity = Number(value.quantity);
          }
          const details = value.details.trim();
          if (value.have && details) {
            item.details = details;
          }
          if (vm.recommendedValue) {
            item.recommended = vm.recommendedValue;
          }
          return item;
        }),
      })),
    };
  }

  skipStep = async () => {
    try {
      await this.markStepCompleted();
      this.navigateBack();
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  private async markStepCompleted() {
    await this.client.addMetadata({
      key: `onboarding_step.${this.addressId}.${this.stepId}`,
      value: 'completed'
    });
  }

  private navigateBack() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('getting-started');
    }
  }
}
